import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  FileUp,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import { studentService } from "../services/studentService";
import Export from "../assets/export.svg";
import DeactivateStudentModal from "../components/modals/DeactivateStudentModal";
import InviteStudentModal from "../components/modals/InviteStudentModal";
import ImportRosterModal from "../components/modals/ImportRosterModal";
import ResendInviteModal from "../components/modals/ResendInviteModal";
import DetailStudentModal from "../components/modals/DetailStudentModal";
import ReferenceLetterModal from "../components/modals/ReferenceLetterModal";
import DepartStudentModal from "../components/modals/DepartStudentModal";
import EditStudentModal from "../components/modals/EditStudentModal";
import DeleteStudentModal from "../components/modals/DeleteStudentModal";
import RemoveInvitesModal from "../components/modals/RemoveInvitesModal";
import Dropdown from "../components/ui/Dropdown";
import Engagement from "./Engagement";
import { useSchoolRole, canManageStudents, canWriteReferenceLetters } from "../hooks/useSchoolRole";

const TABS = ["Active Students", "Pending Students", "Departed Students", "Engagement"];
const TAB_LABELS = {
  "Active Students": "manageStudents.tabs.activeStudents",
  "Pending Students": "manageStudents.tabs.pendingStudents",
  "Departed Students": "manageStudents.tabs.departedStudents",
  Engagement: "manageStudents.tabs.engagement",
};

const formatDate = (value, locale, fallback = "-") => {
  if (!value) return fallback;
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Teaching empty state for the roster. A brand-new school lands on an empty
// Active Students table — instead of a dead-end "no students" line, point at
// the three ways in (import / invite / join code). When the table is empty
// only because a search or filter matched nothing, show a neutral message
// instead (no "get started" framing — they already have students).
const RosterEmptyState = ({ filtered, onImport, onInvite }) => {
  const { t } = useTranslation();

  if (filtered) {
    return (
      <div className="px-6 py-16 flex flex-col items-center text-center gap-2">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-50 text-gray-400">
          <Search size={20} />
        </div>
        <p className="text-base font-semibold text-gray-900">
          {t("manageStudents.empty.noMatch", { defaultValue: "No students match your search" })}
        </p>
        <p className="text-sm text-gray-400 max-w-sm">
          {t("manageStudents.empty.noMatchHint", {
            defaultValue: "Try a different name or email, or clear the search and filter.",
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 py-16 flex flex-col items-center text-center gap-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
        <Users size={22} />
      </div>
      <div>
        <h4 className="text-base font-bold text-gray-900">
          {t("manageStudents.empty.studentsTitle", { defaultValue: "No students yet" })}
        </h4>
        <p className="text-sm text-gray-500 mt-1 max-w-md">
          {t("manageStudents.empty.studentsHint", {
            defaultValue:
              "Add your students to get started — import a class roster, invite them by email, or share your join code & QR below.",
          })}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onImport}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:border-primary hover:text-primary transition-colors"
        >
          <FileUp size={15} />
          {t("manageStudents.importRoster", { defaultValue: "Import roster" })}
        </button>
        <button
          type="button"
          onClick={onInvite}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600 transition-colors"
        >
          <Plus size={15} />
          {t("manageStudents.inviteStudent", { defaultValue: "Invite student" })}
        </button>
      </div>
    </div>
  );
};

const StudentRow = ({ student, onToggleStatus, onView, onDepart, onEdit, onActivate, onDelete, onReferenceLetter, canManage, canWriteReference }) => {
  const { t, i18n } = useTranslation();
  const isActive = student.status?.toUpperCase() === "ACTIVE";
  const studentName = `${student.firstName || ""} ${student.lastName || ""}`.trim() || student.slug || t("manageStudents.na");
  const formattedDate = formatDate(student.createdAt, i18n.resolvedLanguage || "en", t("manageStudents.na"));

  const handleViewProfile = () => {
    // Canonical env var is VITE_STUDENT_URL (shared app-wide);
    // VITE_STUDENT_PORTAL_URL is a legacy fallback from before the
    // student-URL env names were consolidated.
    const studentPortalDomain = import.meta.env.VITE_STUDENT_URL || import.meta.env.VITE_STUDENT_PORTAL_URL || "http://localhost:3000";
    if (student.slug) {
      // Stable public profile URL — works for every student, whether or not
      // they've generated a share link. (The old /pub/<slug> fallback 404'd:
      // /pub only resolves a ProfileShare shareId, never a slug.)
      window.open(`${studentPortalDomain}/u/${student.slug}`, "_blank");
    } else if (onView) {
      // No slug yet (student hasn't finished onboarding) — open the in-app
      // detail panel instead of a dead link.
      onView(student);
    } else {
      toast.error(t("manageStudents.studentProfileUnavailable"));
    }
  };

  return (
    <tr className="h-16 hover:bg-gray-50 transition-colors">
      <td className="px-6 text-gray-900 text-xs">{studentName}</td>
      <td className="px-6 text-gray-900 text-xs">{student.email}</td>
      <td className="px-6 text-gray-900 text-xs">{student.grade || t("manageStudents.na")}</td>
      <td className="px-6 text-gray-900 text-xs">{formattedDate}</td>
      <td className="px-6">
        <span
          className={`px-2.5 py-0.5 rounded-[10px] text-xs font-medium capitalize ${
            isActive
              ? "bg-emerald-100 text-emerald-800"
              : "bg-[#FEF3C7] text-[#92400E]"
          }`}
        >
          {student.status}
        </span>
      </td>
      <td className="px-6">
        <div className="flex items-center gap-3">
          <button 
            type="button"
            onClick={handleViewProfile}
            className="px-2 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-md transition-colors"
          >
            {t("manageStudents.actions.viewHypebook", { defaultValue: "View Hypebook" })}
          </button>
          {/* AI-drafted reference letter from the student's record. COUNSELOR+
              (backend-gated); only for active students — the school doesn't
              speak for someone it has deactivated. */}
          {isActive && canWriteReference ? (
            <button
              type="button"
              onClick={() => onReferenceLetter(student)}
              className="px-2 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-medium rounded-md transition-colors"
            >
              {t("manageStudents.actions.referenceLetter", { defaultValue: "Reference letter" })}
            </button>
          ) : null}
          {/* Edit / Deactivate / Depart are ADMIN+ actions (backend-gated).
              Hidden for confirmed lower ranks; shown otherwise (fail-open). */}
          {canManage ? (
            <button
              type="button"
              onClick={() => onEdit(student)}
              className="px-2 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-md transition-colors"
            >
              {t("manageStudents.actions.edit", { defaultValue: "Edit" })}
            </button>
          ) : null}
          {isActive && canManage ? (
            <button
              type="button"
              onClick={() => onToggleStatus(student)}
              className="px-2 py-2 bg-red-600 hover:bg-red-700 text-red-50 text-xs font-medium rounded-md transition-colors"
            >
              {t("manageStudents.actions.deactivate")}
            </button>
          ) : null}
          {isActive && canManage ? (
            <button
              type="button"
              onClick={() => onDepart(student)}
              className="px-2 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-md transition-colors"
            >
              {t("manageStudents.actions.depart")}
            </button>
          ) : null}
          {/* Re-activate a deactivated (archived) student. Non-destructive, so
              no confirm — flips status back to ACTIVE. */}
          {!isActive && canManage ? (
            <button
              type="button"
              onClick={() => onActivate(student)}
              className="px-2 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-md transition-colors"
            >
              {t("manageStudents.actions.activate")}
            </button>
          ) : null}
          {/* Permanent delete — visually de-emphasized (red outline) so it
              isn't fat-fingered; the modal is the real guard. */}
          {canManage ? (
            <button
              type="button"
              onClick={() => onDelete(student)}
              className="px-2 py-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 text-xs font-medium rounded-md transition-colors"
            >
              {t("manageStudents.actions.delete", { defaultValue: "Delete" })}
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
};

const PendingInviteRow = ({ invite, onResend, onCancel, selected, onToggleSelect }) => {
  const { t, i18n } = useTranslation();
  const status = (invite.status || "").toLowerCase();
  const isPending = status === "pending";
  const isExpired = status === "expired";
  const isCancelled = status === "cancelled";
  // Both of these leave emailSentAt null, so they must be told apart or an
  // undeliverable address masquerades as a pending to-do forever:
  //   emailFailedAt set → we tried and the provider rejected it
  //   otherwise        → "Import only", never attempted
  const sendFailed = isPending && !invite.emailSentAt && !!invite.emailFailedAt;
  const notInvited = isPending && !invite.emailSentAt && !sendFailed;
  const formattedDate = formatDate(invite.createdAt, i18n.resolvedLanguage || "en", t("manageStudents.na"));

  return (
    <tr className="h-16 hover:bg-gray-50 transition-colors">
      <td className="pl-6 pr-2 py-4">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(invite.id)}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/30 cursor-pointer"
          aria-label={t("manageStudents.invites.selectRow", { defaultValue: "Select invite" })}
        />
      </td>
      <td className="px-6 py-4 text-gray-900 text-xs">{invite.email}</td>
      <td className="px-6 py-4 text-gray-900 text-xs">{formattedDate}</td>
      <td className="px-6 py-4">
        <span
          // Failure reason on hover — provider errors name the actual problem
          // ("invalid recipient", "domain not found"), which is what an admin
          // needs to decide between fixing the address and dropping the row.
          title={sendFailed ? invite.emailError || undefined : undefined}
          className={`px-2.5 py-0.5 rounded-[10px] text-xs font-medium ${
            sendFailed
              ? "bg-red-100 text-red-800"
              : notInvited
              ? "bg-amber-100 text-amber-800"
              : isPending
              ? "bg-blue-100 text-blue-800"
              : isExpired
              ? "bg-red-100 text-red-800"
              : "bg-emerald-100 text-emerald-800"
          }`}
        >
          {sendFailed ? (
            t("manageStudents.invites.invalidEmail", { defaultValue: "Invalid email" })
          ) : notInvited ? (
            t("manageStudents.invites.notInvited", { defaultValue: "Not invited yet" })
          ) : (
            <span className="capitalize">{status}</span>
          )}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onResend(invite)}
            className="px-2 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-md transition-colors"
          >
            {notInvited
              ? t("manageStudents.actions.send", { defaultValue: "Send" })
              : t("manageStudents.actions.resend")}
          </button>
          {/* Cancel an invite that's still live — invalidates the link. Hidden
              once it's already cancelled (Resend stays, to re-activate it). */}
          {!isCancelled ? (
            <button
              type="button"
              onClick={() => onCancel(invite)}
              className="px-2 py-2 bg-white border border-gray-300 hover:border-red-400 hover:text-red-600 text-gray-700 text-xs font-medium rounded-md transition-colors"
            >
              {t("manageStudents.actions.cancel", { defaultValue: "Cancel" })}
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
};

const SortButton = ({ columnKey, label, sortConfig, onSort }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-[10px]">
      <div className="flex flex-col cursor-pointer gap-[2px]">
        <button
          type="button"
          onClick={() => onSort(columnKey, "asc")}
          className="leading-none hover:opacity-80"
        >
          <svg
            width="10"
            height="6"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`${
              sortConfig.key === columnKey && sortConfig.direction === "asc"
                ? "opacity-100"
                : "opacity-70"
            }`}
          >
            <title>{t("manageStudents.sortAscending", { label })}</title>
            <path d="M6 0L11.1962 7.5H0.803848L6 0Z" fill="currentColor" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => onSort(columnKey, "desc")}
          className="leading-none hover:opacity-80"
        >
          <svg
            width="10"
            height="6"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`${
              sortConfig.key === columnKey && sortConfig.direction === "desc"
                ? "opacity-100"
                : "opacity-70"
            }`}
          >
            <title>{t("manageStudents.sortDescending", { label })}</title>
            <path d="M6 8L0.803848 0.5H11.1962L6 8Z" fill="currentColor" />
          </svg>
        </button>
      </div>
      {label}
    </div>
  );
};

// Row for the "Departed Students" tab — read-only. Shows who left, why, and
// when (plus who departed them), with a link to their (now personal) Hypebook.
const DepartedStudentRow = ({ departure }) => {
  const { t, i18n } = useTranslation();
  const name = `${departure.firstName || ""} ${departure.lastName || ""}`.trim() || departure.slug || t("manageStudents.na");
  const departedDate = formatDate(departure.departedAt, i18n.resolvedLanguage || "en", t("manageStudents.na"));
  // OTHER carries a free-text note; the presets have i18n labels shared with
  // the depart modal.
  const reasonLabel =
    departure.reasonPreset === "OTHER" && departure.reasonCustom
      ? departure.reasonCustom
      : t(`modals.departStudent.reasons.${(departure.reasonPreset || "").toLowerCase()}`, {
          defaultValue: departure.reasonPreset || t("manageStudents.na"),
        });

  const handleViewProfile = () => {
    // Canonical env var is VITE_STUDENT_URL (shared app-wide);
    // VITE_STUDENT_PORTAL_URL is a legacy fallback from before the
    // student-URL env names were consolidated.
    const studentPortalDomain = import.meta.env.VITE_STUDENT_URL || import.meta.env.VITE_STUDENT_PORTAL_URL || "http://localhost:3000";
    if (departure.slug) {
      window.open(`${studentPortalDomain}/u/${departure.slug}`, "_blank");
    } else {
      toast.error(t("manageStudents.studentProfileUnavailable"));
    }
  };

  return (
    <tr className="h-16 hover:bg-gray-50 transition-colors">
      <td className="px-6 text-gray-900 text-xs">{name}</td>
      <td className="px-6 text-gray-900 text-xs">{departure.email}</td>
      <td className="px-6 text-gray-900 text-xs">{departure.grade || t("manageStudents.na")}</td>
      <td className="px-6 text-gray-900 text-xs">
        <span className="px-2.5 py-0.5 rounded-[10px] text-xs font-medium bg-gray-100 text-gray-700">{reasonLabel}</span>
      </td>
      <td className="px-6 text-gray-900 text-xs">
        <div className="flex flex-col">
          <span>{departedDate}</span>
          {departure.departedByName ? (
            <span className="text-[11px] text-gray-400">
              {t("manageStudents.departed.by", { defaultValue: "by" })} {departure.departedByName}
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-6">
        <button
          type="button"
          onClick={handleViewProfile}
          className="px-2 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-md transition-colors"
        >
          {t("manageStudents.actions.viewHypebook", { defaultValue: "View Hypebook" })}
        </button>
      </td>
    </tr>
  );
};

const ManageStudents = () => {
  const { t, i18n } = useTranslation();
  // Deactivate / depart are ADMIN+ on the backend; viewing, inviting, and
  // exporting are open to all roles. Gate just the management actions, failing
  // open on an unknown role.
  const schoolRole = useSchoolRole();
  const canManage = canManageStudents(schoolRole);
  const canWriteReference = canWriteReferenceLetters(schoolRole);
  const [referenceLetterStudent, setReferenceLetterStudent] = useState(null);
  // Allow deep-linking to a specific tab via ?tab=pending (used by the
  // dashboard stat cards). Only consulted for the initial tab.
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get("tab");
    if (tab === "pending") return "Pending Students";
    if (tab === "departed") return "Departed Students";
    if (tab === "engagement") return "Engagement";
    return "Active Students";
  });
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showImportRosterModal, setShowImportRosterModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDepartModal, setShowDepartModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeparting, setIsDeparting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [departedStudents, setDepartedStudents] = useState([]);
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  // Bulk-select for the Pending Students tab → "Send invites".
  const [selectedInviteIds, setSelectedInviteIds] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [showRemoveInvitesModal, setShowRemoveInvitesModal] = useState(false);
  // Default to Active so the "Active Students" tab is honest — inactive
  // (archived) students are reached by switching this dropdown to Inactive.
  const [activeStatusFilter, setActiveStatusFilter] = useState("active");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    totalAchievements: 0,
    activeStudents: 0,
    pendingInvites: 0,
    schoolName: "",
    adminName: "",
    // Engagement metrics (school-scoped, from the dashboard stats API).
    totalStudents: 0,
    studentsWithAchievements: 0,
    studentsWithHypepage: 0,
    studentsWithResume: 0,
    verifiedAchievements: 0,
    hypesReceived: 0,
  });

  // Fetch students from API
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await studentService.getStudents({
        limit: itemsPerPage,
        page: currentPage,
        search: searchQuery,
        status: activeStatusFilter,
      });

      setStudents(response.data);
      setTotalPages(response.meta.totalPages);
      setTotalCount(response.meta.total);
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedFetchStudents"));
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, currentPage, searchQuery, activeStatusFilter, t]);

  // Fetch invites from API
  const fetchInvites = useCallback(async () => {
    setLoading(true);
    try {
      const response = await studentService.getInvites({
        search: searchQuery,
        userRole: "STUDENT",
        excludeAccepted: true,
      });

      // Backend returns array directly; only keep student invites that belong on pending tab.
      const allInvites = (response || []).filter((invite) => {
        const role = (invite.userRole || "").toUpperCase();
        const status = (invite.status || "").toUpperCase();
        if (role !== "STUDENT") return false;
        return status === "PENDING" || status === "EXPIRED" || status === "CANCELLED";
      });
      const startIdx = (currentPage - 1) * itemsPerPage;
      const endIdx = startIdx + itemsPerPage;
      const paginatedInvites = allInvites.slice(startIdx, endIdx);

      setPendingInvites(paginatedInvites);
      setSelectedInviteIds([]);
      setTotalCount(allInvites.length);
      setTotalPages(Math.ceil(allInvites.length / itemsPerPage));
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedFetchInvites"));
      console.error("Error fetching invites:", error);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, currentPage, searchQuery, t]);

  // Fetch departed students from API (the StudentSchoolDeparture log).
  const fetchDeparted = useCallback(async () => {
    setLoading(true);
    try {
      const response = await studentService.getDepartedStudents({
        limit: itemsPerPage,
        page: currentPage,
        search: searchQuery,
      });

      setDepartedStudents(response.data);
      setTotalPages(response.meta.totalPages);
      setTotalCount(response.meta.total);
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedFetchDeparted", { defaultValue: "Failed to fetch departed students" }));
      console.error("Error fetching departed students:", error);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, currentPage, searchQuery, t]);

  // Fetch dashboard stats from API
  const fetchStats = useCallback(async () => {
    try {
      const response = await studentService.getDashboardStats();
      const adminName = [response.adminFirstName, response.adminLastName].filter(Boolean).join(" ");

      setStats({
        totalAchievements: response.totalAchievements || 0,
        activeStudents: response.activeStudents || 0,
        pendingInvites: response.pendingInvites || 0,
        schoolName: response.schoolName || "",
        adminName,
        totalStudents: response.totalStudents || 0,
        studentsWithAchievements: response.studentsWithAchievements || 0,
        studentsWithHypepage: response.studentsWithHypepage || 0,
        studentsWithResume: response.studentsWithResume || 0,
        verifiedAchievements: response.verifiedAchievements || 0,
        hypesReceived: response.hypesReceived || 0,
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    }
  }, []);

  // Fetch data when tab, page, search, or filter changes
  useEffect(() => {
    if (activeTab === "Active Students") {
      fetchStudents();
    } else if (activeTab === "Pending Students") {
      fetchInvites();
    } else if (activeTab === "Departed Students") {
      fetchDeparted();
    }
  }, [activeTab, fetchStudents, fetchInvites, fetchDeparted]);

  // Fetch dashboard stats on mount (used for the page Header's school/admin name)
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const filteredStudents = useMemo(() => {
    return students;
  }, [students]);

  const handleToggleStatus = (student) => {
    setSelectedStudent(student);
    // Activate functionality - commented out for future use
    // if (student.status === "inactive") {
    //   setShowActivateModal(true);
    // } else {
      setShowDeactivateModal(true);
    // }
  };

  const handleConfirmDeactivation = async () => {
    if (selectedStudent) {
      try {
        await studentService.deactivateStudent(selectedStudent.id);
        toast.success(t("manageStudents.toasts.studentDeactivated"));
        fetchStudents();
        fetchStats();
      } catch (error) {
        toast.error(error.message || t("manageStudents.toasts.failedDeactivateStudent"));
        console.error("Error deactivating student:", error);
      }
    }
    setShowDeactivateModal(false);
    setSelectedStudent(null);
  };

  const handleActivateStudent = async (student) => {
    try {
      await studentService.activateStudent(student.id);
      toast.success(t("manageStudents.toasts.studentActivated", { defaultValue: "Student activated" }));
      fetchStudents();
      fetchStats();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedActivateStudent", { defaultValue: "Failed to activate student" }));
      console.error("Error activating student:", error);
    }
  };

  const handleDeleteStudent = (student) => {
    setSelectedStudent(student);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedStudent) return;
    try {
      setIsDeleting(true);
      await studentService.deleteStudent(selectedStudent.id);
      toast.success(t("manageStudents.toasts.studentDeleted", { defaultValue: "Student deleted" }));
      setShowDeleteModal(false);
      setSelectedStudent(null);
      fetchStudents();
      fetchStats();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedDeleteStudent", { defaultValue: "Failed to delete student" }));
      console.error("Error deleting student:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleInviteStudent = async (emails) => {
    try {
      await studentService.inviteStudents(emails);
      toast.success(t("manageStudents.toasts.invitedStudents", { count: emails.length }));
      if (activeTab === "Pending Students") {
        fetchInvites();
      }
      fetchStats();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedInviteStudents"));
      console.error("Error inviting students:", error);
    }
    setShowInviteModal(false);
  };

  const handleResendInvite = (invite) => {
    setSelectedInvite(invite);
    setShowResendModal(true);
  };

  const toggleInviteSelected = (inviteId) => {
    setSelectedInviteIds((prev) => (prev.includes(inviteId) ? prev.filter((id) => id !== inviteId) : [...prev, inviteId]));
  };

  const toggleAllInvitesSelected = () => {
    setSelectedInviteIds((prev) => (prev.length === pendingInvites.length ? [] : pendingInvites.map((i) => i.id)));
  };

  // Bulk-send invite emails for the selected pending invites (incl. import-only
  // rows that were never emailed). Reuses the resend path server-side.
  const handleBulkSendInvites = async () => {
    if (selectedInviteIds.length === 0) return;
    setBulkSending(true);
    try {
      const { sent } = await studentService.sendStudentInvites(selectedInviteIds);
      toast.success(t("manageStudents.toasts.invitesSent", { defaultValue: "Sent {{count}} invite(s).", count: sent }));
      setSelectedInviteIds([]);
      fetchInvites();
      fetchStats();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedSendInvites", { defaultValue: "Failed to send invites" }));
      console.error("Error bulk-sending invites:", error);
    } finally {
      setBulkSending(false);
    }
  };

  // Permanently remove the selected pending invites (roster mistake cleanup) —
  // opens a styled confirm modal; the delete runs on confirm.
  const handleBulkRemoveInvites = () => {
    if (selectedInviteIds.length === 0) return;
    setShowRemoveInvitesModal(true);
  };

  const handleConfirmRemoveInvites = async () => {
    setBulkSending(true);
    try {
      const { deleted } = await studentService.deleteStudentInvites(selectedInviteIds);
      toast.success(t("manageStudents.toasts.invitesRemoved", { defaultValue: "Removed {{count}} invite(s).", count: deleted }));
      setSelectedInviteIds([]);
      setShowRemoveInvitesModal(false);
      fetchInvites();
      fetchStats();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedRemoveInvites", { defaultValue: "Failed to remove invites" }));
      console.error("Error removing invites:", error);
    } finally {
      setBulkSending(false);
    }
  };

  const handleCancelInvite = async (invite) => {
    // Cancelling invalidates the recipient's link, so confirm first.
    const ok = window.confirm(
      t("manageStudents.confirmCancelInvite", {
        defaultValue: "Cancel the invite for {{email}}? Their invite link will stop working.",
        email: invite.email,
      }),
    );
    if (!ok) return;
    try {
      await studentService.cancelInvite(invite.id);
      toast.success(t("manageStudents.toasts.inviteCancelled", { defaultValue: "Invite cancelled" }));
      fetchInvites();
      fetchStats();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedCancelInvite", { defaultValue: "Failed to cancel invite" }));
      console.error("Error cancelling invite:", error);
    }
  };

  const handleConfirmResend = async () => {
    if (selectedInvite) {
      try {
        await studentService.resendInvite(selectedInvite.id);
        toast.success(t("manageStudents.toasts.inviteResent"));
        fetchInvites();
        fetchStats();
      } catch (error) {
        toast.error(error.message || t("manageStudents.toasts.failedResendInvite"));
        console.error("Error resending invite:", error);
      }
    }
    setShowResendModal(false);
    setSelectedInvite(null);
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setShowDetailModal(true);
  };

  const handleDepartStudent = (student) => {
    setSelectedStudent(student);
    setShowDepartModal(true);
  };

  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setShowEditModal(true);
  };

  const handleConfirmEditStudent = async (student, payload) => {
    try {
      await studentService.updateStudent(student.id, payload);
      toast.success(t("manageStudents.toasts.studentUpdated"));
      setShowEditModal(false);
      setSelectedStudent(null);
      fetchStudents();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedUpdateStudent"));
      console.error("Error updating student:", error);
    }
  };

  const handleConfirmDepartStudent = async (reasonPreset, reasonCustom, overrideNoPersonalEmail = false) => {
    if (!selectedStudent) return;

    try {
      setIsDeparting(true);
      await studentService.departStudent(selectedStudent.id, reasonPreset, reasonCustom || undefined, overrideNoPersonalEmail);
      toast.success(t("manageStudents.toasts.studentDeparted"));
      setShowDepartModal(false);
      setShowDetailModal(false);
      setSelectedStudent(null);
      fetchStudents();
      fetchStats();
    } catch (error) {
      toast.error(error.message || t("manageStudents.toasts.failedDepartStudent"));
      console.error("Error departing student:", error);
    } finally {
      setIsDeparting(false);
    }
  };

  const handleExport = async () => {
    if (activeTab !== "Active Students") {
      toast.info(t("manageStudents.toasts.exportOnlyActive"));
      return;
    }

    try {
      setExporting(true);
      const response = await studentService.exportStudents({
        search: searchQuery || undefined,
        status: activeStatusFilter !== "all" ? activeStatusFilter : undefined,
      });

      const studentsToExport = response.data || [];
      if (studentsToExport.length === 0) {
        toast.info(t("manageStudents.toasts.noStudentsToExport"));
        return;
      }

      const rows = studentsToExport.map((student) => ({
        ID: student.id || "",
        Email: student.email || "",
        "First Name": student.firstName || "",
        "Last Name": student.lastName || "",
        Grade: student.grade || "",
        "Date of Birth": student.dob || "",
        "Created At": formatDate(student.createdAt, i18n.resolvedLanguage || "en", ""),
        Status: student.status || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `students_export_${dateStamp}.xlsx`);
      toast.success(response.message || t("manageStudents.toasts.studentsExported"));
    } catch (error) {
      console.error("Error exporting students:", error);
      toast.error(error.message || t("manageStudents.toasts.failedExportStudents"));
    } finally {
      setExporting(false);
    }
  };

  // Export individual student - commented out for future use
  // const handleExportStudent = async () => {
  //   if (selectedStudent) {
  //     try {
  //       // TODO: Implement individual student export from detail modal
  //       toast.info("Individual export functionality coming soon");
  //     } catch (error) {
  //       toast.error("Failed to export student data");
  //       console.error("Error exporting student:", error);
  //     }
  //   }
  // };

  const handleToggleStatusFromModal = (student) => {
    setShowDetailModal(false);
    handleToggleStatus(student);
  };

  const filteredPendingInvites = useMemo(() => {
    return pendingInvites;
  }, [pendingInvites]);

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const sortedStudents = useMemo(() => {
    const sortableStudents = [...filteredStudents];
    if (sortConfig.key !== null) {
      sortableStudents.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle name sorting
        if (sortConfig.key === "name") {
          aVal = `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.slug || "";
          bVal = `${b.firstName || ""} ${b.lastName || ""}`.trim() || b.slug || "";
        }

        // Handle date sorting
        if (sortConfig.key === "dateJoined" || sortConfig.key === "createdAt") {
          aVal = new Date(a.createdAt || 0).getTime();
          bVal = new Date(b.createdAt || 0).getTime();
        }

        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableStudents;
  }, [filteredStudents, sortConfig]);

  const sortedPendingInvites = useMemo(() => {
    const sortableInvites = [...filteredPendingInvites];
    if (sortConfig.key !== null) {
      sortableInvites.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle date sorting
        if (sortConfig.key === "dateInvited" || sortConfig.key === "createdAt") {
          aVal = new Date(a.createdAt || 0).getTime();
          bVal = new Date(b.createdAt || 0).getTime();
        }

        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableInvites;
  }, [filteredPendingInvites, sortConfig]);

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    if (activeTab === "Active Students") {
      fetchStudents();
    } else if (activeTab === "Pending Students") {
      fetchInvites();
    } else if (activeTab === "Departed Students") {
      fetchDeparted();
    }
  };

  // Handle search on Enter key
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Reset pagination when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchQuery("");
    setSortConfig({ key: null, direction: "asc" });
  };

  return (
    <div className="bg-white min-h-screen font-manrope pb-[100px]">
      <Sidebar />
      <Header schoolName={stats.schoolName} adminName={stats.adminName} />

      <div className="ml-[23px] mt-6 px-8 flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-0.5 font-['Manrope']">
            <h2 className="text-gray-900 text-2xl font-bold">{t("manageStudents.titleStudents")}</h2>
            <p className="text-gray-500 text-base font-normal">{t("manageStudents.subtitleStudents")}</p>
          </div>
          {/* Hidden in the empty "No students yet" state — RosterEmptyState
              below shows its own Import/Invite CTAs there, so these would be a
              redundant double set. Kept for populated and filtered-empty views
              (and the Pending tab), where the empty state has no CTA. */}
          {(loading ||
            activeTab !== "Active Students" ||
            sortedStudents.length > 0 ||
            Boolean(searchQuery) ||
            activeStatusFilter === "inactive") && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowImportRosterModal(true)}
                className="h-11 pl-3 pr-4 py-1.5 bg-white border border-gray-300 hover:border-primary hover:text-primary rounded-lg flex items-center gap-1.5 text-gray-700 font-medium transition-colors"
                title={t("manageStudents.importRosterTip", {
                  defaultValue: "Upload a CSV / PDF / image — AI extracts the roster and you review before sending invites.",
                })}
              >
                <FileUp size={16} />
                {t("manageStudents.importRoster", { defaultValue: "Import roster" })}
              </button>
              <button
                type="button"
                onClick={() => setShowInviteModal(true)}
                className="h-11 pl-3 pr-4 py-1.5 bg-primary hover:bg-blue-600 rounded-lg flex items-center gap-1.5 text-white font-medium transition-colors"
              >
                <Plus size={16} />
                {t("manageStudents.inviteStudent")}
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-6 mt-6">
          <div className="border-b border-gray-200 flex gap-8">
            {TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => handleTabChange(tab)}
                className={`pb-4 text-lg font-semibold border-b-2 transition-all ${
                  activeTab === tab
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-900 hover:text-gray-600"
                }`}
              >
                {t(TAB_LABELS[tab], { defaultValue: tab })}
              </button>
            ))}
          </div>

          {/* The Engagement tab carries its own search + filters, so hide the
              Manage-Students toolbar there. */}
          {activeTab !== "Engagement" && (
          <div className="flex justify-between items-center gap-4">
            <div className="w-96 h-12 bg-white rounded-lg outline outline-1 outline-blue-100 flex overflow-hidden">
              <input
                type="text"
                placeholder={t("manageStudents.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="flex-1 px-4 py-3 text-gray-500 font-medium outline-none"
              />
              <button
                type="button"
                onClick={handleSearch}
                className="px-6 bg-secondary border-l border-blue-100 flex items-center gap-2 text-gray-500 font-medium hover:bg-blue-100 transition-colors"
              >
                <Search size={18} />
                {t("manageStudents.search")}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting || activeTab !== "Active Students"}
                className={`h-11 px-3 py-1.5 bg-primary hover:bg-blue-600 rounded-lg flex items-center gap-1.5 text-white text-base font-medium transition-colors ${
                  exporting || activeTab !== "Active Students" ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <img src={Export} alt={"Export"} className="w-5 h-5" />
                {exporting ? t("manageStudents.exporting") : t("manageStudents.export")}
              </button>

              {activeTab === "Active Students" && (
                <Dropdown
                  options={[
                    { label: t("manageStudents.filter.allStatus"), value: "all" },
                    { label: t("manageStudents.filter.active"), value: "active" },
                    { label: t("manageStudents.filter.inactive"), value: "inactive" },
                  ]}
                  value={activeStatusFilter}
                  onChange={(value) => {
                    setActiveStatusFilter(value);
                    setCurrentPage(1);
                  }}
                  placeholder={t("manageStudents.selectStatus")}
                  width="w-48"
                />
              )}
            </div>
          </div>
          )}

          {activeTab === "Engagement" ? (
            // Engagement panel manages its own loading + pagination.
            <Engagement embedded />
          ) : loading ? (
            <div className="bg-white rounded-xl outline outline-1 outline-blue-100 p-12 flex justify-center items-center">
              <div className="text-gray-500 text-lg">{t("manageStudents.loading")}</div>
            </div>
          ) : activeTab === "Active Students" ? (
            <div className="bg-white rounded-xl outline outline-1 outline-blue-100 overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-secondary">
                  <tr className="text-gray-900 text-xs font-semibold">
                    <th className="px-6 py-3 w-[20%]">
                      <SortButton
                        columnKey="name"
                        label={t("manageStudents.columns.studentName")}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[25%]">
                      <SortButton
                        columnKey="email"
                        label={t("manageStudents.columns.email")}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[12%]">
                      <SortButton
                        columnKey="grade"
                        label={t("manageStudents.columns.grade")}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[15%]">
                      <SortButton
                        columnKey="createdAt"
                        label={t("manageStudents.columns.dateJoined")}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[10%]">
                      <SortButton
                        columnKey="status"
                        label={t("manageStudents.columns.status")}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[18%]">{t("manageStudents.columns.action")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {sortedStudents.length > 0 ? (
                    sortedStudents.map((student, idx) => (
                      <StudentRow
                        key={student.id || idx}
                        student={student}
                        onToggleStatus={handleToggleStatus}
                        onView={handleViewStudent}
                        onDepart={handleDepartStudent}
                        onEdit={handleEditStudent}
                        onActivate={handleActivateStudent}
                        onDelete={handleDeleteStudent}
                        onReferenceLetter={(s) => setReferenceLetterStudent(s)}
                        canManage={canManage}
                        canWriteReference={canWriteReference}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="p-0">
                        <RosterEmptyState
                          filtered={Boolean(searchQuery) || activeStatusFilter === "inactive"}
                          onImport={() => setShowImportRosterModal(true)}
                          onInvite={() => setShowInviteModal(true)}
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : activeTab === "Pending Students" ? (
            <div className="bg-white rounded-xl outline outline-1 outline-blue-100 overflow-hidden">
              {/* Bulk-send action bar — appears once invites are selected. */}
              {selectedInviteIds.length > 0 ? (
                <div className="flex items-center justify-between gap-3 bg-blue-50 border-b border-blue-100 px-6 py-3">
                  <span className="text-sm font-medium text-gray-700">
                    {t("manageStudents.invites.selectedCount", { defaultValue: "{{count}} selected", count: selectedInviteIds.length })}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleBulkRemoveInvites}
                      disabled={bulkSending}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
                    >
                      {t("manageStudents.invites.removeSelected", { defaultValue: "Remove" })}
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkSendInvites}
                      disabled={bulkSending}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-blue-600 disabled:opacity-50"
                    >
                      {bulkSending
                        ? t("manageStudents.invites.sending", { defaultValue: "Sending…" })
                        : t("manageStudents.invites.sendSelected", { defaultValue: "Send {{count}} invite(s)", count: selectedInviteIds.length })}
                    </button>
                  </div>
                </div>
              ) : null}
              <table className="w-full text-left">
                <thead className="bg-secondary">
                  <tr className="text-gray-900 text-xs font-semibold">
                    <th className="pl-6 pr-2 py-3 w-[44px]">
                      <input
                        type="checkbox"
                        checked={pendingInvites.length > 0 && selectedInviteIds.length === pendingInvites.length}
                        ref={(el) => {
                          if (el) el.indeterminate = selectedInviteIds.length > 0 && selectedInviteIds.length < pendingInvites.length;
                        }}
                        onChange={toggleAllInvitesSelected}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/30 cursor-pointer"
                        aria-label={t("manageStudents.invites.selectAll", { defaultValue: "Select all invites" })}
                      />
                    </th>
                    <th className="px-6 py-3 w-[32%]">
                      <SortButton
                        columnKey="email"
                        label={t("manageStudents.columns.email")}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[35%]">
                      <SortButton
                        columnKey="createdAt"
                        label={t("manageStudents.columns.dateInvited")}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[15%]">
                      <SortButton
                        columnKey="status"
                        label={t("manageStudents.columns.status")}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                    </th>
                    <th className="px-6 py-3 w-[15%]">{t("manageStudents.columns.action")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {sortedPendingInvites.length > 0 ? (
                    sortedPendingInvites.map((invite, idx) => (
                      <PendingInviteRow
                        key={invite.id || idx}
                        invite={invite}
                        onResend={handleResendInvite}
                        onCancel={handleCancelInvite}
                        selected={selectedInviteIds.includes(invite.id)}
                        onToggleSelect={toggleInviteSelected}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        {t("manageStudents.empty.invites")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : activeTab === "Departed Students" ? (
            <div className="bg-white rounded-xl outline outline-1 outline-blue-100 overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-secondary">
                  <tr className="text-gray-900 text-xs font-semibold">
                    <th className="px-6 py-3 w-[20%]">{t("manageStudents.columns.studentName")}</th>
                    <th className="px-6 py-3 w-[22%]">{t("manageStudents.columns.email")}</th>
                    <th className="px-6 py-3 w-[10%]">{t("manageStudents.columns.grade")}</th>
                    <th className="px-6 py-3 w-[18%]">{t("manageStudents.columns.reason", { defaultValue: "Reason" })}</th>
                    <th className="px-6 py-3 w-[18%]">{t("manageStudents.columns.departed", { defaultValue: "Departed" })}</th>
                    <th className="px-6 py-3 w-[12%]">{t("manageStudents.columns.action")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {departedStudents.length > 0 ? (
                    departedStudents.map((departure, idx) => (
                      <DepartedStudentRow key={departure.id || idx} departure={departure} />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        {t("manageStudents.empty.departed", { defaultValue: "No students have departed yet." })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : null}

          {/* Engagement has its own pagination; hide the shared footer there. */}
          {activeTab !== "Engagement" && (
          <div className="flex justify-between items-center font-['Manrope']">
            <div className="flex items-center gap-2">
              <span className="text-gray-900 text-sm">{t("manageStudents.resultsPerPage")}</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="w-20 h-10 rounded-full outline outline-1 outline-blue-100 px-4 text-gray-500 text-sm cursor-pointer hover:bg-blue-50 transition-colors"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-2 bg-white rounded-full outline outline-1 outline-blue-100 p-1">
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="flex items-center">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-8 h-8 flex items-center justify-center text-sm font-medium rounded-full ${
                        currentPage === pageNum
                          ? "bg-blue-100 text-gray-900"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <span className="text-gray-900 text-sm font-normal">
              {t("manageStudents.showingResults", {
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(currentPage * itemsPerPage, totalCount),
                total: totalCount,
              })}
            </span>
          </div>
          )}
        </div>
      </div>

      {/* Activate functionality - commented out for future use
      {showActivateModal && (
        <ActivateStudentModal
          onClose={() => {
            setShowActivateModal(false);
            setSelectedStudent(null);
          }}
          onConfirm={handleConfirmActivation}
        />
      )}
      */}

      {showDeactivateModal && (
        <DeactivateStudentModal
          onClose={() => {
            setShowDeactivateModal(false);
            setSelectedStudent(null);
          }}
          onConfirm={handleConfirmDeactivation}
        />
      )}

      {showInviteModal && (
        <InviteStudentModal
          onClose={() => setShowInviteModal(false)}
          onConfirm={handleInviteStudent}
        />
      )}

      {showImportRosterModal && (
        <ImportRosterModal
          onClose={() => setShowImportRosterModal(false)}
          // After invites are sent, refresh the student / invite lists
          // so the freshly-imported batch shows up immediately in the
          // pending-invites tab. Re-uses the existing fetchers — the
          // backend Promise.all flow on InviteService dedupes already.
          onImportComplete={() => {
            void fetchStudents();
            void fetchInvites();
          }}
        />
      )}

      {showResendModal && (
        <ResendInviteModal
          onClose={() => {
            setShowResendModal(false);
            setSelectedInvite(null);
          }}
          onConfirm={handleConfirmResend}
          inviteEmail={selectedInvite?.email}
        />
      )}

      {referenceLetterStudent && <ReferenceLetterModal student={referenceLetterStudent} onClose={() => setReferenceLetterStudent(null)} />}

      {showDetailModal && (
        <DetailStudentModal
          onClose={() => {
            setShowDetailModal(false);
            setSelectedStudent(null);
          }}
          student={selectedStudent}
          canManage={canManage}
          onToggleStatus={handleToggleStatusFromModal}
          onDepartStudent={handleDepartStudent}
          // Export functionality - commented out for future use
          // onExport={handleExportStudent}
          // exporting={false}
        />
      )}

      <DepartStudentModal
        isOpen={showDepartModal}
        student={selectedStudent}
        isSubmitting={isDeparting}
        onClose={() => {
          if (!isDeparting) {
            setShowDepartModal(false);
          }
        }}
        onConfirm={handleConfirmDepartStudent}
      />

      {showEditModal && (
        <EditStudentModal
          student={selectedStudent}
          onClose={() => {
            setShowEditModal(false);
            setSelectedStudent(null);
          }}
          onConfirm={handleConfirmEditStudent}
        />
      )}

      {showDeleteModal && (
        <DeleteStudentModal
          student={selectedStudent}
          isDeleting={isDeleting}
          onClose={() => {
            if (!isDeleting) {
              setShowDeleteModal(false);
              setSelectedStudent(null);
            }
          }}
          onConfirm={handleConfirmDelete}
        />
      )}

      {showRemoveInvitesModal && (
        <RemoveInvitesModal
          count={selectedInviteIds.length}
          isRemoving={bulkSending}
          onClose={() => {
            if (!bulkSending) setShowRemoveInvitesModal(false);
          }}
          onConfirm={handleConfirmRemoveInvites}
        />
      )}
    </div>
  );
};

export default ManageStudents;
